import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Card,
  H2,
  H4,
  Paragraph,
  SizableText,
  XStack,
  YStack,
  Separator,
} from 'tamagui';
import { Link } from 'react-router-dom';
import { SkeletonCard, SkeletonText, SkeletonPage } from '../components/Skeleton';
import type { MaintenanceScheduleEntry } from '@cartrack/shared';
import { api, type Vehicle } from '../lib/api';
import {
  nextUpcoming,
  statusColor,
  statusLabel,
  summarizeSchedule,
} from '../lib/schedule-view';
import { unitSuffix, useUnitSystem } from '../lib/user-prefs';

export function Dashboard() {
  const vehiclesQuery = useQuery({ queryKey: ['vehicles'], queryFn: api.listVehicles });
  const vehicles = vehiclesQuery.data ?? [];

  const scheduleQueries = useQueries({
    queries: vehicles.map((v) => ({
      queryKey: ['schedule', v.id],
      queryFn: () => api.getSchedule(v.id),
      staleTime: 30_000,
    })),
  });

  if (vehiclesQuery.isLoading) {
    return (
      <SkeletonPage headingWidth={180}>
        <XStack gap="$3" flexWrap="wrap">
          <SkeletonCard maxWidth={360} $gtSm={{ width: 320 }} />
          <SkeletonCard maxWidth={360} $gtSm={{ width: 320 }} />
        </XStack>
      </SkeletonPage>
    );
  }
  if (vehiclesQuery.isError)
    return <Paragraph color="$danger">Failed to load vehicles.</Paragraph>;

  return (
    <YStack gap="$4">
      <H2>Dashboard</H2>
      {vehicles.length === 0 ? (
        <Card padding="$4" elevate>
          <YStack gap="$2">
            <H4>No vehicles yet</H4>
            <Paragraph color="$color10">
              Add your first vehicle to start tracking maintenance.
            </Paragraph>
            <Link to="/vehicles" style={LINK_RESET}>
              <SizableText color="$accent" size="$3" marginTop="$2">
                Go to Vehicles →
              </SizableText>
            </Link>
          </YStack>
        </Card>
      ) : (
        <XStack gap="$3" flexWrap="wrap">
          {vehicles.map((v, idx) => (
            <VehicleDashboardCard
              key={v.id}
              vehicle={v}
              schedule={scheduleQueries[idx]?.data}
              loading={scheduleQueries[idx]?.isLoading ?? false}
              error={Boolean(scheduleQueries[idx]?.isError)}
            />
          ))}
        </XStack>
      )}
    </YStack>
  );
}

function VehicleDashboardCard({
  vehicle,
  schedule,
  loading,
  error,
}: {
  vehicle: Vehicle;
  schedule: MaintenanceScheduleEntry[] | undefined;
  loading: boolean;
  error: boolean;
}) {
  const unit = useUnitSystem();
  const suffix = unitSuffix(unit);
  const summary = schedule ? summarizeSchedule(schedule) : null;
  const next = schedule ? nextUpcoming(schedule) : null;
  const hasOverdue = (summary?.overdue ?? 0) > 0;

  return (
    <Card
      padding="$4"
      elevate
      // Fluid on mobile (fills the column, no overflow at 375px); fixed-width
      // cards that wrap into a grid from small breakpoints up.
      width="100%"
      maxWidth={360}
      $gtSm={{ width: 320 }}
    >
      <YStack gap="$2">
        {hasOverdue ? (
          <YStack
            backgroundColor="$red4"
            borderRadius="$3"
            paddingHorizontal="$3"
            paddingVertical="$2"
            marginBottom="$1"
          >
            <Paragraph color="$red10" fontWeight="700" size="$2">
              {summary!.overdue} overdue service{summary!.overdue === 1 ? '' : 's'}
            </Paragraph>
          </YStack>
        ) : null}

        <H4>
          {vehicle.year} {vehicle.make} {vehicle.model}
        </H4>
        <Paragraph color="$color10" size="$2">
          {vehicle.nickname ?? vehicle.vin ?? 'No nickname'} ·{' '}
          {vehicle.currentMileage.toLocaleString()} {suffix}
        </Paragraph>

        <Separator marginVertical="$2" />

        {loading ? (
          <SkeletonText />
        ) : error ? (
          <Paragraph color="$red10" size="$2">
            Couldn't load schedule.
          </Paragraph>
        ) : summary && schedule ? (
          <>
            <XStack gap="$3">
              <StatusCount label="Overdue" count={summary.overdue} status="OVERDUE" />
              <StatusCount label="Due soon" count={summary.dueSoon} status="DUE_SOON" />
              <StatusCount label="Upcoming" count={summary.upcoming} status="UPCOMING" />
            </XStack>
            {next ? (
              <Paragraph size="$2" color="$color10" marginTop="$2">
                Next: <strong>{next.serviceType}</strong>
                {next.milesUntilDue !== null
                  ? ` in ${next.milesUntilDue.toLocaleString()} ${suffix}`
                  : ''}
                {next.daysUntilDue !== null ? ` (${next.daysUntilDue} days)` : ''} ·{' '}
                <Paragraph display="inline" color={statusColor(next.status)} size="$2">
                  {statusLabel(next.status)}
                </Paragraph>
              </Paragraph>
            ) : (
              <Paragraph color="$green10" size="$2" marginTop="$2">
                All good
              </Paragraph>
            )}
          </>
        ) : null}

        <Link to={`/vehicles/${vehicle.id}`} style={LINK_RESET}>
          <SizableText color="$accent" size="$3" marginTop="$2">
            Open vehicle →
          </SizableText>
        </Link>
      </YStack>
    </Card>
  );
}

// Link reset so the Tamagui child owns its (theme-aware) accent color.
const LINK_RESET: React.CSSProperties = { textDecoration: 'none', color: 'inherit' };

function StatusCount({
  label,
  count,
  status,
}: {
  label: string;
  count: number;
  status: 'OVERDUE' | 'DUE_SOON' | 'UPCOMING';
}) {
  return (
    <YStack alignItems="center" gap="$0" minWidth={64}>
      <Paragraph color={statusColor(status)} fontWeight="700" size="$6">
        {count}
      </Paragraph>
      <Paragraph size="$1" color="$color10">
        {label}
      </Paragraph>
    </YStack>
  );
}
